/*
 * The quote page's top and its added sections, like Jobber (owner, 2026-10-31). The top keeps the client in one card and
 * the quote's own facts beside it, with a pencil for the name and number. Below the lines, Add section puts an
 * introduction, the client message, images, reviews and attachments on the quote; the client sees them on the quote page
 * (SectionsShown, also used there).
 */
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  isSectionFile,
  isSectionImage,
  MAX_QUOTE_REVIEWS,
  MAX_SECTION_FILES,
  PRICE_OFTEN_WORDS,
  QUOTE_SECTION_WORDS,
  type PriceOften,
  type QuoteDetail,
  type QuoteReview,
  type QuoteSection,
  type QuoteSectionKind,
  type SectionFile,
} from "@steward/shared";
import { api, pictureUrl } from "../api/client";
import { useGbp } from "../api/marketing";
import { useSession } from "../app/session";
import { moneyExact, phoneText, shortDate } from "../components/format";
import { Icon } from "../components/Icon";
import { Menu, useToast } from "../components/overlays";
import { Button, Details, IconButton } from "../components/parts";
import { errText } from "../components/work";
import { AddressLines, DirectionsChoice, openSavedDirections } from "../components/Directions";

const localDay = (iso: string) => new Date(iso).toLocaleDateString("en-CA");
const OFTEN_OF: Record<string, PriceOften> = { "one-time": "once", weekly: "weekly", biweekly: "biweekly", monthly: "monthly" };
const sizeWords = (b: number) =>
  b < 1024 * 1024 ? `${String(Math.max(1, Math.round(b / 1024)))} KB` : `${(b / 1048576).toFixed(1)} MB`;

/** Saves through the server and puts the answer straight into the open quote. */
function useQuoteSave(id: string) {
  const qc = useQueryClient();
  return async (method: "PUT" | "PATCH", path: string, body: unknown) => {
    const after = await api<QuoteDetail>(method, `quotes/${id}/${path}`, body);
    qc.setQueryData(["quote", id], after);
    void qc.invalidateQueries({ queryKey: ["quotes"] });
    return after;
  };
}

// The top: client card and quote facts ------------------------------------------------------------------------------

export function QuoteTop({ q, editable }: { q: QuoteDetail; editable: boolean }) {
  const navigate = useNavigate();
  const toast = useToast();
  const save = useQuoteSave(q.id);
  const [edit, setEdit] = useState<{ title: string; number: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [maps, setMaps] = useState<string | null>(null);
  const go = async () => {
    if (!edit) return;
    const number = Number(edit.number);
    if (!Number.isInteger(number) || number < 1) {
      toast("Type a whole quote number.", { kind: "error" });
      return;
    }
    setBusy(true);
    try {
      await save("PATCH", "header", { title: edit.title, number });
      setEdit(null);
      toast("Quote saved");
    } catch (e) {
      toast(errText(e), { kind: "error" });
    } finally {
      setBusy(false);
    }
  };
  const openClient = () => void navigate({ to: "/clients/$id", params: { id: q.clientId ?? "" } });
  const rows: [ReactNode, ReactNode][] = [
    [
      "Quote #",
      edit ? (
        <input
          className="qtop-input num"
          inputMode="numeric"
          aria-label="Quote number"
          value={edit.number}
          onChange={(e) => setEdit({ ...edit, number: e.target.value.replace(/\D/g, "") })}
        />
      ) : (
        String(q.number)
      ),
    ],
    ["Created", shortDate(localDay(q.createdAt))],
    ...(q.approvedAt ? [["Approved", shortDate(localDay(q.approvedAt))] as [ReactNode, ReactNode]] : []),
    ["Sent", q.sentAt ? shortDate(localDay(q.sentAt)) : "Not yet"],
    [
      "Viewed",
      q.views && q.lastViewedAt
        ? `${shortDate(localDay(q.lastViewedAt))}${q.views > 1 ? `, ${String(q.views)} times` : ""}`
        : "Not yet",
    ],
    ["How often", q.frequency === "one-time" ? "One time" : PRICE_OFTEN_WORDS[OFTEN_OF[q.frequency] ?? "once"]],
    ["Good until", q.expiresOn ? shortDate(q.expiresOn) : "No end"],
    [
      "Deposit",
      q.deposit ? `${moneyExact(q.depositCents)}${q.deposit.kind === "percent" ? ` (${String(q.deposit.value)}%)` : ""}` : "None",
    ],
    ["Card to approve", q.cardRequired ? "Needed" : "Not needed"],
  ];
  return (
    <div className="qtop">
      {edit ? (
        <label className="qtop-title">
          <span>Quote title</span>
          <input
            autoFocus
            value={edit.title}
            maxLength={200}
            placeholder="Like Move out cleaning"
            onChange={(e) => setEdit({ ...edit, title: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Enter") void go();
            }}
          />
        </label>
      ) : null}
      <section className="card qtop-client">
        <div className="qtop-client-head">
          {q.clientId ? (
            <button type="button" className="link qtop-name" onClick={openClient}>
              {q.name}
            </button>
          ) : (
            <b className="qtop-name">{q.name}</b>
          )}
          <Menu button={({ toggle }) => <IconButton icon="more" label="Client options" onClick={toggle} />}>
            {(close) => (
              <>
                {q.clientId ? (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      close();
                      openClient();
                    }}
                  >
                    Open client
                  </button>
                ) : null}
                {q.address ? (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      close();
                      if (q.address && !openSavedDirections(q.address)) setMaps(q.address);
                    }}
                  >
                    Directions
                  </button>
                ) : null}
              </>
            )}
          </Menu>
          <DirectionsChoice address={maps} onClose={() => setMaps(null)} />
        </div>
        {q.address ? (
          <div className="qtop-line">
            <span className="muted small">Property address</span>
            <span>
              <AddressLines address={q.address} />
            </span>
          </div>
        ) : null}
        <div className="qtop-contact">
          {q.contactPhone ? <a href={`tel:${q.contactPhone}`}>{phoneText(q.contactPhone)}</a> : null}
          {q.contactEmail ? <a href={`mailto:${q.contactEmail}`}>{q.contactEmail}</a> : null}
          {!q.contactPhone && !q.contactEmail ? <span className="muted">No phone or email</span> : null}
        </div>
      </section>
      <section className="qtop-facts">
        {editable && !edit ? (
          <div className="qtop-pencil">
            <IconButton
              icon="edit"
              label="Edit quote details"
              onClick={() => setEdit({ title: q.title ?? "", number: String(q.number) })}
            />
          </div>
        ) : null}
        <Details rows={rows} />
        {edit ? (
          <div className="row end">
            <Button type="secondary" onClick={() => setEdit(null)}>
              Cancel
            </Button>
            <Button loading={busy} onClick={() => void go()}>
              Save
            </Button>
          </div>
        ) : null}
      </section>
    </div>
  );
}

// Sections ---------------------------------------------------------------------------------------------------------

type Part = QuoteSectionKind | "message";
const ORDER: Part[] = ["intro", "message", "images", "reviews", "attachments"];
const PART_WORDS: Record<Part, string> = { ...QUOTE_SECTION_WORDS, message: "Client message" };
const HELP: Partial<Record<Part, string>> = {
  intro: "A picture and a few words at the top of the quote",
  images: "Add images to show your past work",
  attachments: "Include all files for this quote in one place",
  reviews: "Show what other clients say about you",
  message: "A note to the client with this quote",
};
type Draft = QuoteSection | { kind: "message"; text: string };

const emptyOf = (k: Part): Draft =>
  k === "intro"
    ? { kind: "intro", title: "", body: "", imageId: null }
    : k === "images" || k === "attachments"
      ? { kind: k, fileIds: [] }
      : k === "reviews"
        ? { kind: "reviews", reviews: [] }
        : { kind: "message", text: "" };

/** The office's part of the quote page: saved sections, their editors and the Add section bar. */
export function QuoteSections({ q, editable }: { q: QuoteDetail; editable: boolean }) {
  const toast = useToast();
  const save = useQuoteSave(q.id);
  const [open, setOpen] = useState<Partial<Record<Part, Draft>>>({});
  const [busy, setBusy] = useState<Part | null>(null);
  // Files uploaded while editing, so a new picture shows before the section is saved.
  const [added, setAdded] = useState<SectionFile[]>([]);
  const files = [...q.sectionFiles, ...added.filter((a) => !q.sectionFiles.some((f) => f.id === a.id))];
  const saved = (k: Part): Draft | null =>
    k === "message" ? (q.message ? { kind: "message", text: q.message } : null) : (q.sections.find((s) => s.kind === k) ?? null);
  const shown = ORDER.filter((k) => Boolean(saved(k) ?? open[k]));
  const missing = ORDER.filter((k) => !saved(k) && !open[k]);

  const put = async (sections: QuoteSection[], message: string | null) =>
    save("PUT", "sections", {
      sections: [...sections].sort((a, b) => ORDER.indexOf(a.kind) - ORDER.indexOf(b.kind)),
      message: message ?? "",
    });
  const keep = async (k: Part) => {
    const d = open[k];
    if (!d) return;
    setBusy(k);
    try {
      if (d.kind === "message") await put(q.sections, d.text);
      else await put([...q.sections.filter((s) => s.kind !== k), d], q.message);
      setOpen(({ [k]: _done, ...rest }) => rest);
      toast(`${PART_WORDS[k]} saved`);
    } catch (e) {
      toast(errText(e), { kind: "error" });
    } finally {
      setBusy(null);
    }
  };
  const drop = async (k: Part) => {
    if (!saved(k)) {
      setOpen(({ [k]: _gone, ...rest }) => rest);
      return;
    }
    const before = { sections: q.sections, message: q.message };
    try {
      if (k === "message") await put(q.sections, null);
      else
        await put(
          q.sections.filter((s) => s.kind !== k),
          q.message,
        );
      setOpen(({ [k]: _gone, ...rest }) => rest);
      toast(`${PART_WORDS[k]} taken off`, { undo: () => void put(before.sections, before.message) });
    } catch (e) {
      toast(errText(e), { kind: "error" });
    }
  };

  return (
    <>
      {shown.map((k) => {
        const d = open[k];
        const s = saved(k);
        const head = (
          <div className="qsec-head">
            <h2>{PART_WORDS[k]}</h2>
            {editable ? (
              <div className="row tight">
                {!d ? (
                  <IconButton
                    icon="edit"
                    label={`Edit ${PART_WORDS[k].toLowerCase()}`}
                    onClick={() => setOpen({ ...open, [k]: s ?? emptyOf(k) })}
                  />
                ) : null}
                <IconButton icon="trash" label={`Take off ${PART_WORDS[k].toLowerCase()}`} onClick={() => void drop(k)} />
              </div>
            ) : null}
          </div>
        );
        return (
          <section className="card qsec" key={k}>
            <div className="card-body">
              {head}
              {d ? (
                <>
                  <SectionEditor
                    quoteId={q.id}
                    draft={d}
                    files={files}
                    onFile={(f) => setAdded((a) => [...a, f])}
                    change={(next) => setOpen((o) => ({ ...o, [k]: next }))}
                  />
                  <div className="row end">
                    <Button type="secondary" onClick={() => setOpen(({ [k]: _x, ...rest }) => rest)}>
                      Cancel
                    </Button>
                    <Button loading={busy === k} onClick={() => void keep(k)}>
                      Save
                    </Button>
                  </div>
                </>
              ) : s ? (
                s.kind === "message" ? (
                  <p className="pre-wrap">{s.text}</p>
                ) : (
                  <SectionBody s={s} files={files} src={(id) => pictureUrl(`files/${id}`)} />
                )
              ) : null}
            </div>
          </section>
        );
      })}
      {editable && missing.length ? (
        <div className="qsec-add" role="group" aria-label="Add section">
          <span className="qsec-add-label">
            <Icon name="plus" size={18} /> Add section
          </span>
          {missing.map((k) => (
            <button key={k} type="button" className="qsec-chip" onClick={() => setOpen({ ...open, [k]: emptyOf(k) })}>
              {PART_WORDS[k]}
            </button>
          ))}
        </div>
      ) : null}
    </>
  );
}

function SectionEditor({
  quoteId,
  draft,
  files,
  onFile,
  change,
}: {
  quoteId: string;
  draft: Draft;
  files: SectionFile[];
  onFile: (f: SectionFile) => void;
  change: (d: Draft) => void;
}) {
  const help = HELP[draft.kind];
  if (draft.kind === "message")
    return (
      <label className="field wide">
        <span className="label">{help}</span>
        <textarea rows={4} maxLength={2000} value={draft.text} onChange={(e) => change({ ...draft, text: e.target.value })} />
      </label>
    );
  if (draft.kind === "intro")
    return (
      <div className="stack">
        {draft.imageId ? (
          <div className="qsec-pics one">
            <Pic
              id={draft.imageId}
              files={files}
              src={(id) => pictureUrl(`files/${id}`)}
              remove={() => change({ ...draft, imageId: null })}
            />
          </div>
        ) : (
          <DropZone
            quoteId={quoteId}
            pictures
            left={1}
            onFile={(f) => {
              onFile(f);
              change({ ...draft, imageId: f.id });
            }}
          />
        )}
        <label className="field wide">
          <span className="label">Title</span>
          <input maxLength={200} value={draft.title} onChange={(e) => change({ ...draft, title: e.target.value })} />
        </label>
        <label className="field wide">
          <span className="label">Description</span>
          <textarea rows={4} maxLength={4000} value={draft.body} onChange={(e) => change({ ...draft, body: e.target.value })} />
        </label>
      </div>
    );
  if (draft.kind === "reviews") return <ReviewsEditor draft={draft} change={change} />;
  const pictures = draft.kind === "images";
  const left = MAX_SECTION_FILES - draft.fileIds.length;
  return (
    <div className="stack">
      <div className="qsec-sub">
        <span className="muted small">{help}</span>
        <span className="muted small">
          {draft.fileIds.length} of {MAX_SECTION_FILES} uploaded
        </span>
      </div>
      {draft.fileIds.length ? (
        pictures ? (
          <div className="qsec-pics">
            {draft.fileIds.map((id) => (
              <Pic
                key={id}
                id={id}
                files={files}
                src={(x) => pictureUrl(`files/${x}`)}
                remove={() => change({ ...draft, fileIds: draft.fileIds.filter((x) => x !== id) })}
              />
            ))}
          </div>
        ) : (
          <FileList
            ids={draft.fileIds}
            files={files}
            remove={(id) => change({ ...draft, fileIds: draft.fileIds.filter((x) => x !== id) })}
          />
        )
      ) : null}
      {left > 0 ? (
        <DropZone
          quoteId={quoteId}
          pictures={pictures}
          left={left}
          onFile={(f) => {
            onFile(f);
            change({ ...draft, fileIds: [...draft.fileIds, f.id].slice(0, MAX_SECTION_FILES) });
          }}
        />
      ) : null}
    </div>
  );
}

/** Jobber's dashed box: a button to pick files, or drop them on it. Each file uploads to the quote right away. */
function DropZone({
  quoteId,
  pictures,
  left,
  onFile,
}: {
  quoteId: string;
  pictures: boolean;
  left: number;
  onFile: (f: SectionFile) => void;
}) {
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const send = async (list: File[]) => {
    setBusy(true);
    for (const f of list.slice(0, left)) {
      const mime = f.type || "application/octet-stream";
      if (pictures ? !isSectionImage(mime) : !isSectionFile(mime)) {
        toast(`${f.name} is not a file type the quote can show.`, { kind: "error" });
        continue;
      }
      if (f.size > 10 * 1024 * 1024) {
        toast(`${f.name} is too big. The most is 10 MB.`, { kind: "error" });
        continue;
      }
      try {
        const data = await new Promise<string>((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(typeof r.result === "string" ? (r.result.split(",")[1] ?? "") : "");
          r.onerror = () => reject(new Error("That file could not be read."));
          r.readAsDataURL(f);
        });
        const row = await api<SectionFile>("POST", `quotes/${quoteId}/files`, { name: f.name, mime, dataBase64: data });
        onFile({ id: row.id, name: row.name, mime: row.mime, size: row.size });
      } catch (e) {
        toast(errText(e, `${f.name} didn't upload. Try again.`), { kind: "error" });
      }
    }
    if (list.length > left) toast(`Only ${String(left)} more fit here.`);
    setBusy(false);
  };
  return (
    <div
      className={`qsec-drop${over ? " over" : ""}`}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        void send(Array.from(e.dataTransfer.files));
      }}
    >
      <input
        ref={input}
        type="file"
        hidden
        multiple={left > 1}
        accept={pictures ? "image/avif,image/gif,image/jpeg,image/png,image/webp" : "image/*,.heic,.pdf,.docx"}
        onChange={(e) => {
          const list = Array.from(e.target.files ?? []);
          e.target.value = "";
          void send(list);
        }}
      />
      <Button type="secondary" size="small" loading={busy} onClick={() => input.current?.click()}>
        {pictures ? (left === 1 ? "Add image" : "Add images") : "Select files"}
      </Button>
      <span className="muted small">
        {pictures ? "AVIF, GIF, JPEG, PNG, WEBP up to 10 MB each" : "JPEG, PNG, HEIC, PDF, DOCX up to 10 MB each"}
      </span>
    </div>
  );
}

function ReviewsEditor({ draft, change }: { draft: Extract<Draft, { kind: "reviews" }>; change: (d: Draft) => void }) {
  const { company, can } = useSession();
  const gbp = useGbp(company.id);
  const [typed, setTyped] = useState<QuoteReview>({ author: "", stars: 5, text: "", postedOn: null });
  const full = draft.reviews.length >= MAX_QUOTE_REVIEWS;
  const add = (r: QuoteReview) => change({ ...draft, reviews: [...draft.reviews, r].slice(0, MAX_QUOTE_REVIEWS) });
  const picked = (author: string, text: string) => draft.reviews.some((r) => r.author === author && r.text === text);
  const google = can("marketing.read")
    ? (gbp.data?.reviews ?? []).filter((r) => r.stars >= 4 && r.text.trim() && !picked(r.author, r.text))
    : [];
  return (
    <div className="stack">
      <span className="muted small">
        {HELP.reviews}. {draft.reviews.length} of {MAX_QUOTE_REVIEWS}.
      </span>
      {draft.reviews.length ? (
        <div className="qrev-grid">
          {draft.reviews.map((r, i) => (
            <ReviewCard
              key={`${r.author}-${String(i)}`}
              r={r}
              remove={() => change({ ...draft, reviews: draft.reviews.filter((_, j) => j !== i) })}
            />
          ))}
        </div>
      ) : null}
      {!full && google.length ? (
        <div className="qrev-pick">
          <b className="small">From your Google reviews</b>
          {google.slice(0, 12).map((r) => (
            <div key={r.id} className="qrev-pick-row">
              <Stars n={r.stars} />
              <span className="what">
                <b>{r.author}</b> <span className="muted">{r.text}</span>
              </span>
              <Button
                type="tertiary"
                size="small"
                icon="plus"
                onClick={() =>
                  add({ author: r.author, stars: r.stars, text: r.text.slice(0, 1000), postedOn: r.postedAt.slice(0, 10) })
                }
              >
                Add
              </Button>
            </div>
          ))}
        </div>
      ) : null}
      {!full ? (
        <div className="qrev-type">
          <b className="small">Type one in</b>
          <div className="row">
            <label className="field">
              <span className="label">Name</span>
              <input maxLength={80} value={typed.author} onChange={(e) => setTyped({ ...typed, author: e.target.value })} />
            </label>
            <label className="field">
              <span className="label">Stars</span>
              <select value={typed.stars} onChange={(e) => setTyped({ ...typed, stars: Number(e.target.value) })}>
                {[5, 4, 3, 2, 1].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span className="label">Date</span>
              <input
                type="date"
                value={typed.postedOn ?? ""}
                onChange={(e) => setTyped({ ...typed, postedOn: e.target.value || null })}
              />
            </label>
          </div>
          <label className="field wide">
            <span className="label">What they said</span>
            <textarea
              rows={3}
              maxLength={1000}
              value={typed.text}
              onChange={(e) => setTyped({ ...typed, text: e.target.value })}
            />
          </label>
          <div className="row end">
            <Button
              type="secondary"
              size="small"
              disabled={!typed.author.trim() || !typed.text.trim()}
              onClick={() => {
                add({ ...typed, author: typed.author.trim(), text: typed.text.trim() });
                setTyped({ author: "", stars: 5, text: "", postedOn: null });
              }}
            >
              Add review
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// What the sections look like (office and client) ---------------------------------------------------------------------

export function Stars({ n }: { n: number }) {
  return (
    <span className="qrev-stars" role="img" aria-label={`${String(n)} out of 5 stars`}>
      {"★".repeat(n)}
      <span className="off">{"★".repeat(5 - n)}</span>
    </span>
  );
}

function ReviewCard({ r, remove }: { r: QuoteReview; remove?: () => void }) {
  return (
    <div className="qrev">
      <div className="qrev-who">
        <span className="qrev-dot" aria-hidden>
          {r.author.trim().charAt(0).toUpperCase()}
        </span>
        <b>{r.author}</b>
        {r.postedOn ? <span className="muted small">{shortDate(r.postedOn)}</span> : null}
        {remove ? <IconButton icon="close" label={`Take off ${r.author}'s review`} size={16} onClick={remove} /> : null}
      </div>
      <Stars n={r.stars} />
      <p>{r.text}</p>
    </div>
  );
}

/** A picture: a link the browser can show (office pictures need the company header, so they come in as blobs). */
function Pic({
  id,
  files,
  src,
  remove,
}: {
  id: string;
  files: SectionFile[];
  src: (id: string) => Promise<string | null> | string | null;
  remove?: () => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let made: string | null = null;
    let live = true;
    void Promise.resolve(src(id)).then((u) => {
      if (u?.startsWith("blob:")) made = u;
      if (live) setUrl(u);
    });
    return () => {
      live = false;
      if (made) URL.revokeObjectURL(made);
    };
    // src is a new function each render; the picture only changes with its id.
  }, [id]);
  const name = files.find((f) => f.id === id)?.name ?? "Picture";
  return (
    <figure className="qsec-pic">
      {url ? <img src={url} alt={name} loading="lazy" /> : <span className="qsec-pic-wait" role="img" aria-label={name} />}
      {remove ? (
        <button type="button" className="qsec-pic-x" aria-label={`Take off ${name}`} onClick={remove}>
          <Icon name="close" size={16} />
        </button>
      ) : null}
    </figure>
  );
}

function FileList({
  ids,
  files,
  remove,
  href,
}: {
  ids: string[];
  files: SectionFile[];
  remove?: (id: string) => void;
  href?: (id: string) => string;
}) {
  return (
    <ul className="qsec-files">
      {ids.map((id) => {
        const f = files.find((x) => x.id === id);
        if (!f) return null;
        return (
          <li key={id}>
            <Icon name="file" size={18} />
            {href ? (
              <a href={href(id)} target="_blank" rel="noreferrer">
                {f.name}
              </a>
            ) : (
              <span>{f.name}</span>
            )}
            <span className="muted small">{sizeWords(f.size)}</span>
            {remove ? <IconButton icon="close" label={`Take off ${f.name}`} size={16} onClick={() => remove(id)} /> : null}
          </li>
        );
      })}
    </ul>
  );
}

/** One section as it reads (no heading). Files link only where href is given (the client's page). */
function SectionBody({
  s,
  files,
  src,
  href,
}: {
  s: QuoteSection;
  files: SectionFile[];
  src: (id: string) => Promise<string | null> | string | null;
  href?: (id: string) => string;
}) {
  if (s.kind === "intro")
    return (
      <div className="qsec-intro">
        {s.imageId ? <Pic id={s.imageId} files={files} src={src} /> : null}
        {s.title ? <h3>{s.title}</h3> : null}
        {s.body ? <p className="pre-wrap">{s.body}</p> : null}
      </div>
    );
  if (s.kind === "images")
    return (
      <div className="qsec-pics">
        {s.fileIds.map((id) => (
          <Pic key={id} id={id} files={files} src={src} />
        ))}
      </div>
    );
  if (s.kind === "attachments") return <FileList ids={s.fileIds} files={files} {...(href ? { href } : {})} />;
  return (
    <div className="qrev-grid">
      {s.reviews.map((r, i) => (
        <ReviewCard key={`${r.author}-${String(i)}`} r={r} />
      ))}
    </div>
  );
}

/** The sections on the client's quote page. part picks the introduction (top) or the rest (under the price). */
export function SectionsShown({
  sections,
  files,
  src,
  part,
}: {
  sections: QuoteSection[];
  files: SectionFile[];
  /** The public link of a section file. */
  src: (id: string) => string;
  part: "intro" | "rest";
}) {
  const list = sections.filter((s) => (part === "intro" ? s.kind === "intro" : s.kind !== "intro"));
  if (!list.length) return null;
  return (
    <>
      {list.map((s) => (
        <section key={s.kind} className="qsec-public">
          {s.kind !== "intro" ? <h2>{s.kind === "images" ? "Our work" : QUOTE_SECTION_WORDS[s.kind]}</h2> : null}
          <SectionBody s={s} files={files} src={src} href={src} />
        </section>
      ))}
    </>
  );
}
